import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { StreamingAudienceAge } from 'fym-shared/src/music24/streamingSections';
import { TypedMemo } from '../../../../common/types';
import { formatShareBp, topAgeBand } from '../../periodSections';
import { formatStreamingNumber } from '../../streamingFormat';
import { audienceMessages as messages } from '../../audienceMessages';
import { StreamingBlock } from '../../parts/StreamingBlock';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the period sections; each part uses its own slice of it */
import styles from '../../StreamingPeriod.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface AudienceAgeBlockProps {
  age: readonly StreamingAudienceAge[];
  listeners: number | null;
}

/**
 * Age (design streaming-sections a1): one row per band, the largest band in the brand gradient.
 * The bands are the bands of the source: never re-cut them.
 */
const AudienceAgeBlockComponent: React.FC<AudienceAgeBlockProps> = ({ age, listeners }) => {
  const intl = useIntl();
  const top = useMemo(() => topAgeBand(age), [age]);
  const largest = useMemo(() => Math.max(0, ...age.map((band) => band.shareBp)), [age]);

  return (
    <StreamingBlock
      title={intl.formatMessage(messages.age)}
      meta={
        listeners === null ? undefined : intl.formatMessage(messages.listeners, { count: intl.formatNumber(listeners) })
      }
      testId="streaming-audience-age"
    >
      <div className={styles.ageRows}>
        {age.map((band) => (
          <div
            key={band.band}
            className={clsx(styles.ageRow, band.band === top && styles.ageTop)}
            data-top={band.band === top ? 'true' : undefined}
          >
            <span className={styles.ageLabel}>{band.band}</span>
            <span className={styles.ageTrack} aria-hidden>
              <span
                className={styles.ageFill}
                style={{ width: `${largest > 0 ? Math.round((band.shareBp / largest) * 100) : 0}%` }}
              />
            </span>
            <span className={styles.legendShare}>{formatStreamingNumber(intl, band.listeners)}</span>
            <span className={styles.legendCount}>{formatShareBp(intl, band.shareBp)}</span>
          </div>
        ))}
      </div>
    </StreamingBlock>
  );
};

export const AudienceAgeBlock = TypedMemo(AudienceAgeBlockComponent);
